import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import FuzzyFindDialog from 'components/ReportRequestV2/FuzzyFindDialog';
import PasteProductsDialog from 'components/ReportRequestV2/PasteProductsDialog';

import styles from './ProductPicker.module.css';

export interface CatalogProduct {
  id: number;
  name: string;
  workspace: string;
  category: string | null;
  reviews: number;
  /** Already part of the report being updated. */
  original?: boolean;
}

export interface ProductPickerProps {
  catalog: CatalogProduct[];
  loading?: boolean;
  selectedIds: ReadonlySet<number>;
  onAdd: (products: CatalogProduct[]) => void;
  /** The report's workspace: the search starts filtered to it and it is listed first. */
  defaultWorkspace?: string | null;
  /** Workspaces whose data the report may use; their badges are highlighted. */
  allowedWorkspaces?: string[];
  /** Removes products by name; enables Undo after a bulk add. */
  onRemoveNames?: (names: string[]) => void;
  disabled?: boolean;
  error?: string;
}

/** Every workspace row of one product name; reports reference products by name. */
export interface ProductOption {
  name: string;
  rows: CatalogProduct[];
  workspaces: string[];
  category: string | null;
  reviews: number;
}

const SEARCH_LIMIT = 50;
const MAX_BADGES = 4;
/** "Add all matching" is offered up to this many products, so a filter can't add the whole catalog. */
export const ADD_ALL_LIMIT = 100;

/** One option per product name, in catalog order (the catalog sorts the active workspace and newest first). */
export const groupByName = (catalog: CatalogProduct[]): ProductOption[] => {
  const byName = new Map<string, CatalogProduct[]>();
  catalog.forEach((p) => byName.set(p.name, [...(byName.get(p.name) ?? []), p]));
  return Array.from(byName, ([name, rows]) => ({
    name,
    rows,
    workspaces: Array.from(new Set(rows.map((r) => r.workspace))),
    category: rows.find((r) => r.category)?.category ?? null,
    reviews: Math.max(...rows.map((r) => r.reviews)),
  }));
};

/** Values with how many product names carry each, most common first. */
const countBy = (options: ProductOption[], valuesOf: (o: ProductOption) => (string | null)[]) => {
  const counts = new Map<string, number>();
  options.forEach((o) =>
    new Set(valuesOf(o).filter((v): v is string => !!v)).forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1)),
  );
  return counts;
};

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((v) => b.includes(v));

const NO_WORKSPACES: string[] = [];

interface MultiFilterProps {
  label: string;
  counts: Map<string, number>;
  /** Values listed first, in this order, before the rest by count. */
  pinned?: string[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

/** A compact multi-select with a product count per value. */
const MultiFilter: React.FC<MultiFilterProps> = ({ label, counts, pinned = [], value, onChange, disabled }) => {
  const options = React.useMemo(() => {
    const rest = Array.from(counts.keys()).filter((v) => !pinned.includes(v));
    rest.sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a.localeCompare(b));
    return [...pinned.filter((v) => counts.has(v)), ...rest];
  }, [counts, pinned]);
  return (
    <Autocomplete
      multiple
      size="small"
      limitTags={2}
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      disabled={disabled}
      className={styles.filter}
      renderOption={(props, v, { selected }) => (
        <li {...props} key={v}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
          <span className={styles.filterValue}>{v}</span>
          <Typography variant="caption" color="text.secondary">
            {(counts.get(v) ?? 0).toLocaleString()}
          </Typography>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} placeholder={value.length ? '' : 'Any'} />}
    />
  );
};

/** The name with the first occurrence of the search text emphasised. */
const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const q = query.trim();
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className={styles.match}>{text.slice(at, at + q.length)}</mark>
      {text.slice(at + q.length)}
    </>
  );
};

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const NoMatch: React.FC<{ searching: boolean; filtering: boolean; onSearchAll: () => void }> = ({
  searching,
  filtering,
  onSearchAll,
}) => {
  if (!searching) return <>Start typing to search</>;
  if (!filtering) return <>No matching products</>;
  return (
    <Box className={styles.noMatch}>
      <span>No matching products here.</span>
      {/* Keep focus in the search so the list stays open on the wider results. */}
      <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={onSearchAll}>
        Search all workspaces
      </Button>
    </Box>
  );
};

/** Confirms a bulk add, with Undo when the caller can remove products. */
const UndoSnackbar: React.FC<{
  names: string[] | null;
  onUndo?: (names: string[]) => void;
  onClose: () => void;
}> = ({ names, onUndo, onClose }) => (
  <Snackbar
    open={!!names}
    autoHideDuration={8000}
    onClose={(_, reason) => reason !== 'clickaway' && onClose()}
    message={names ? `Added ${plural(names.length, 'product')}` : ''}
    action={
      onUndo && names ? (
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            onUndo(names);
            onClose();
          }}
        >
          Undo
        </Button>
      ) : undefined
    }
  />
);


const filterOptions = createFilterOptions<ProductOption>({
  limit: SEARCH_LIMIT,
  stringify: (o) => `${o.name} ${o.workspaces.join(' ')} ${o.category ?? ''}`,
});

/**
 * Typeahead over the product catalog, one result per product name, plus a bulk
 * paste dialog. Adding a name adds every workspace's row of it, as pasting does;
 * which workspaces' data the report may use is decided by the allowed workspaces.
 */
const ProductPicker: React.FC<ProductPickerProps> = ({
  catalog,
  loading,
  selectedIds,
  onAdd,
  defaultWorkspace,
  allowedWorkspaces = [],
  onRemoveNames,
  disabled,
  error,
}) => {
  const [input, setInput] = React.useState('');
  // The last bulk add, so it can be undone in one click.
  const [lastBulk, setLastBulk] = React.useState<string[] | null>(null);
  // `null` until the user changes the workspace filter: until then it follows the
  // report's workspace, so changing the Project workspace re-scopes the search.
  const [chosenWorkspaces, setWorkspaces] = React.useState<string[] | null>(null);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [pasteOpen, setPasteOpen] = React.useState(false);
  // Open with these names, or closed when `null`.
  const [fuzzyNames, setFuzzyNames] = React.useState<string[] | null>(null);
  const allowed = new Set(allowedWorkspaces);

  const options = React.useMemo(() => groupByName(catalog), [catalog]);
  const workspaceCounts = React.useMemo(() => countBy(options, (o) => o.workspaces), [options]);
  const categoryCounts = React.useMemo(() => countBy(options, (o) => o.rows.map((r) => r.category)), [options]);
  // The default only applies when the catalog has products in that workspace.
  const defaultScope = React.useMemo(
    () => (defaultWorkspace && workspaceCounts.has(defaultWorkspace) ? [defaultWorkspace] : NO_WORKSPACES),
    [defaultWorkspace, workspaceCounts],
  );
  const workspaces = chosenWorkspaces ?? defaultScope;
  const pinnedWorkspaces = React.useMemo(() => (defaultWorkspace ? [defaultWorkspace] : []), [defaultWorkspace]);

  // A name matches when any of its workspace rows is in the selected workspaces and
  // any of its rows is in the selected categories; an empty filter matches everything.
  const filtered = React.useMemo(() => {
    if (workspaces.length === 0 && categories.length === 0) return options;
    return options.filter(
      (o) =>
        (workspaces.length === 0 || o.workspaces.some((w) => workspaces.includes(w))) &&
        (categories.length === 0 || o.rows.some((r) => r.category && categories.includes(r.category))),
    );
  }, [options, workspaces, categories]);
  const filtering = workspaces.length > 0 || categories.length > 0;
  const atDefault = sameSet(workspaces, defaultScope) && categories.length === 0;
  // Products are chosen by name: one selected row means the product is in the set.
  const isAdded = (o: ProductOption) => o.rows.some((r) => selectedIds.has(r.id));
  const addedNames = React.useMemo(
    () => new Set(catalog.filter((p) => selectedIds.has(p.id)).map((p) => p.name)),
    [catalog, selectedIds],
  );
  const addBulk = (products: CatalogProduct[]) => {
    onAdd(products);
    setLastBulk(Array.from(new Set(products.map((p) => p.name))));
  };
  // Bulk add only once the user has narrowed the search, never for the default scope.
  const addable = filtering && !atDefault ? filtered.filter((o) => !isAdded(o)) : [];
  const defaultMissing =
    !!defaultWorkspace && !loading && options.length > 0 && defaultScope.length === 0 && chosenWorkspaces === null;

  return (
    <Box className={styles.picker}>
      <Box className={styles.filters} role="group" aria-label="Filter products">
        <MultiFilter
          label="Workspaces"
          counts={workspaceCounts}
          pinned={pinnedWorkspaces}
          value={workspaces}
          onChange={setWorkspaces}
          disabled={disabled}
        />
        <MultiFilter
          label="Categories"
          counts={categoryCounts}
          value={categories}
          onChange={setCategories}
          disabled={disabled}
        />
        <Box className={styles.presets}>
          {defaultScope.length > 0 && !atDefault && (
            <Chip
              size="small"
              variant="outlined"
              label={`Back to ${defaultWorkspace}`}
              onClick={() => {
                setWorkspaces(null);
                setCategories([]);
              }}
              disabled={disabled}
            />
          )}
          {filtering && (
            <Button
              size="small"
              onClick={() => {
                setWorkspaces([]);
                setCategories([]);
              }}
              disabled={disabled}
            >
              {categories.length > 0 ? 'Clear filters' : 'Search all workspaces'}
            </Button>
          )}
          {defaultMissing && (
            <Typography variant="caption" color="text.secondary">
              {defaultWorkspace} has no products in the catalog, so every workspace is searched.
            </Typography>
          )}
        </Box>
      </Box>
      <Box className={styles.searchRow}>
        <Autocomplete<ProductOption, false, false, false>
          className={styles.search}
          options={filtered}
          loading={loading}
          disabled={disabled}
          value={null}
          inputValue={input}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setInput(value);
          }}
          onChange={(_, option) => {
            if (option) onAdd(option.rows);
          }}
          filterOptions={filterOptions}
          getOptionLabel={(o) => o.name}
          getOptionDisabled={isAdded}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          blurOnSelect={false}
          // Stays open on the same search after adding, so several products can be added in a row.
          disableCloseOnSelect
          clearOnBlur={false}
          noOptionsText={
            <NoMatch
              searching={!!input}
              filtering={filtering}
              onSearchAll={() => {
                setWorkspaces([]);
                setCategories([]);
              }}
            />
          }
          renderOption={(props, o) => (
            <li {...props} key={o.name}>
              <Box className={styles.option}>
                <Box className={styles.optionTitle}>
                  <Typography variant="body2" className={styles.optionName}>
                    {isAdded(o) && <CheckIcon fontSize="inherit" color="success" aria-label="Added" />}
                    <Highlighted text={o.name} query={input} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.category ?? 'Uncategorised'} · {plural(o.reviews, 'review')}
                    {isAdded(o) && ' · already added'}
                  </Typography>
                </Box>
                <Box className={styles.badges}>
                  {o.workspaces.slice(0, MAX_BADGES).map((w) => (
                    <Chip
                      key={w}
                      size="small"
                      label={w}
                      variant={allowed.has(w) ? 'filled' : 'outlined'}
                      className={styles.badge}
                    />
                  ))}
                  {o.workspaces.length > MAX_BADGES && (
                    <Typography variant="caption" color="text.secondary">
                      +{o.workspaces.length - MAX_BADGES}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search products"
              placeholder="Name, workspace or category"
              error={!!error}
              helperText={
                error ??
                (loading
                  ? 'Loading the product catalog…'
                  : `${filtered.length.toLocaleString()}${filtering ? ` of ${options.length.toLocaleString()}` : ''} products; up to ${SEARCH_LIMIT} shown at a time.`)
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          startIcon={<ContentPasteIcon />}
          onClick={() => setPasteOpen(true)}
          disabled={disabled || loading}
          className={styles.pasteButton}
        >
          Paste list
        </Button>
        <Button
          variant="outlined"
          startIcon={<ManageSearchIcon />}
          onClick={() => setFuzzyNames([])}
          disabled={disabled || loading}
          className={styles.pasteButton}
        >
          Fuzzy find
        </Button>
      </Box>
      {addable.length > 0 && (
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              onAdd(addable.flatMap((o) => o.rows));
              setLastBulk(addable.map((o) => o.name));
            }}
            disabled={disabled || addable.length > ADD_ALL_LIMIT}
          >
            Add all {plural(addable.length, 'matching product')}
          </Button>
          {addable.length > ADD_ALL_LIMIT && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Narrow the filters to {ADD_ALL_LIMIT} or fewer products to add them all at once.
            </Typography>
          )}
        </Box>
      )}
      <PasteProductsDialog
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        catalog={catalog}
        onAdd={addBulk}
        addedNames={addedNames}
        onFuzzyFind={setFuzzyNames}
      />
      <FuzzyFindDialog
        open={fuzzyNames !== null}
        onClose={() => setFuzzyNames(null)}
        catalog={catalog}
        onAdd={addBulk}
        initialNames={fuzzyNames ?? undefined}
        addedNames={addedNames}
      />
      <UndoSnackbar names={lastBulk} onUndo={onRemoveNames} onClose={() => setLastBulk(null)} />
    </Box>
  );
};

export default ProductPicker;
